/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

export interface RateLimitConfig {
    /**
     * Recovers the page: called when the countdown reaches zero, and by the manual
     * reload button.
     *
     * A full document reload is the only recovery that works regardless of how a
     * service wrote its loading and error states, so it is the default. Replace it
     * where a service can recover more gently, e.g. by revalidating a route.
     */
    reload: () => void;

    /**
     * True when this service detects 429s itself and calls `reportRateLimit`, so it
     * has no axios interceptor to register.
     *
     * Only silences the dev-time warning about a notice nothing can raise. That
     * warning exists because the notice auto-mounts while registration stays
     * deliberate, which leaves a service able to inherit a notice no 429 will ever
     * reach; a service on the reporting path is not in that trap and should not be
     * told it is.
     */
    hasOwnDetection: boolean;
}

const defaultConfig: RateLimitConfig = {
    reload: () => {
        window.location.reload();
    },
    hasOwnDetection: false,
};

let config = defaultConfig;

/**
 * Overrides how the rate-limit module behaves, for the whole app.
 *
 * Module-level rather than props on the notice, because it is reachable no matter
 * who mounted the notice — services on `ExternalAppProviders` do not mount it
 * themselves. Call it in the same startup block as
 * `registerRateLimitInterceptor`.
 */
export function configureRateLimit(overrides: Partial<RateLimitConfig>) {
    config = { ...config, ...overrides };
}

/** Internal. Read at call time, so a later `configureRateLimit` still applies. */
export function getRateLimitConfig(): RateLimitConfig {
    return config;
}

/**
 * Internal. Recovers the page, through whatever is configured at the moment it is
 * called rather than whatever was configured when the notice mounted.
 */
export function runRateLimitReload() {
    config.reload();
}

/** Internal. Reached by tests through the module's `testing` entry point. */
export function resetRateLimitConfig() {
    config = defaultConfig;
}
